/**
 * Helper functions for Carbon-based worksheets wrapper components ONLY
 * For general worksheets-related helpers, refer to wsHelpers.ts
 */
import { AppShape } from "@eave/shared/types/api";

import APIClient from "web_modules/APIClient";
import { CarbonInputGenerationParams } from "web_modules/shared/carbon-types";

import { WorksheetName, WS_NAME } from "underwriter_dashboard/shared/WorksheetEditor/wsDefs";
import {
  fetchWorksheets,
  getWsByName,
  updateLoanFileWorksheet,
  deleteWSValue,
  updateWSValue,
} from "underwriter_dashboard/shared/WorksheetEditor/wsHelpers";
import { IWorksheetState } from "underwriter_dashboard/shared/WorksheetEditor/interfaces";

// Given a dash `-` string value, will replace the value with `null`
//   In the context of worksheets, this allows us to delete values by sending a dash string
// Note: this directly conflicts with the `notDash` validation rule, so you may want to remove that rule
export const dashToNull = v => (v === "-" ? null : v);

// Primarily for Boolean fields that need to display as string values dash will treat as empty and allow deletion of value.
// Note: this directly conflicts with the `notDash` validation rule, so you may want to remove that rule
export const yesNoStringsToBooleans = v => (v === "-" ? null : v === "Yes" ? true : false);

// Given an empty string value, will replace the value with `null`
//   In the context of worksheets, this allows us to delete values by sending an empty string
// Note: this directly conflicts with the `notEmpty` validation rule, so you may want to remove that rule
export const emptyValuesToNull = v => (v === "" ? null : v);

/**
 * Creates a new worksheet if it doesn't already exist, and returns the new worksheet's ID
 * @param app The AppShape object
 * @param loanFileWs The loan file worksheet
 * @param wsName The specific worksheet name to open to
 */
export const createInitialWorksheet = (app, loanFileWs, wsName: WorksheetName) => {
  // 1. create it, and
  // 2. attach it to the loan_file worksheet

  // 1. creation
  const creationPayload = {
    application_id: app.id,
    worksheet_name: wsName,
  };

  return APIClient.post(`/worksheets`, creationPayload).then(({ worksheet }) => {
    // 2. attach to loan_file ws

    const { id } = worksheet;
    return updateLoanFileWorksheet(loanFileWs, [
      {
        name: wsName,
        value: id,
      },
    ]);
  });
};

export const createWorksheetIfNeeded = (app, worksheets, wsName: WorksheetName) => {
  const specifiedWS = getWsByName(worksheets, wsName);
  if (specifiedWS) {
    return Promise.resolve(worksheets);
  }

  const loanFileWs = getWsByName(worksheets, WS_NAME.LOAN_FILE);
  if (!loanFileWs) {
    // should never happen, as loan_file worksheet is created on loan application creation.
    throw new Error(`could not load loan_file worksheet on app ${app.id}`);
  }

  return createInitialWorksheet(app, loanFileWs, wsName).then(() => fetchWorksheets(app));
};

// Runs through the modalFields objects and applies `prepareForAPI` if present
const prepareUpdatesForAPI = (formUpdates, modalFields: CarbonInputGenerationParams[]) => {
  const preparedUpdates = { ...formUpdates };
  Object.keys(formUpdates).forEach(key => {
    const value = preparedUpdates[key];

    const fieldDef = modalFields.find(field => field.name === key);
    if (fieldDef.prepareForAPI) {
      preparedUpdates[key] = fieldDef.prepareForAPI(value);
    }
  });
  return preparedUpdates;
};

export const updateWorksheet = (
  app: AppShape,
  id,
  version,
  formUpdates,
  modalFields: CarbonInputGenerationParams[],
) => {
  if (!formUpdates) {
    // no updates -- nothing to do.
    return Promise.resolve(null);
  }

  return updateWSValue(app.id, prepareUpdatesForAPI(formUpdates, modalFields), id, version);
};

export const saveWorksheet = (
  app,
  parentWorksheet: IWorksheetState,
  wsRecordName: string,
  wsSliceName: string,
  formUpdates: object, // a map of updated values
  modalFields: CarbonInputGenerationParams[],
) => {
  // worksheet array: creation flow is:
  // 1. creation: create new array item
  // 2. append: append it to the (parent worksheet) array
  // 3. update: add values to that new array item

  // 1. creation
  const creationPayload = {
    application_id: app.id,
    worksheet_name: wsRecordName,
  };

  let newRecordId;
  let newRecordVersion;

  return APIClient.post(`/worksheets`, creationPayload)
    .then(({ worksheet }) => {
      // 2. append

      newRecordId = worksheet.id;
      newRecordVersion = worksheet.version;

      const { id: parentWorksheetId } = parentWorksheet;
      const { version } = worksheet;

      const appendPayload = {
        version,
        updates: [
          {
            name: wsSliceName,
            value: newRecordId,
          },
        ],
      };

      return APIClient.post(`/worksheets/${parentWorksheetId}`, appendPayload);
    })
    .then(() => {
      // 3. update
      return updateWorksheet(app, newRecordId, newRecordVersion, formUpdates, modalFields);
    })
    .catch(err => {
      console.error(err);
    });
};

export const deleteWorksheet = (wsSliceName, parentWorksheet, formState) => {
  const {
    instance: { version: instanceVersion },
    array_index: index,
  } = formState;

  const { id } = parentWorksheet;

  return deleteWSValue(id, wsSliceName, instanceVersion, index);
};
